import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../AppIcon';
import { preferenceEngine } from '../../services/preferenceEngine';

/**
 * QuickOnboarding — First-visit overlay that asks the user what they need.
 * Saves role + intent in preferenceEngine for personalization.
 * Shows only once. Syncs flag to Supabase for cross-device persistence.
 *
 * TODO-BACKEND: reemplazar con endpoint /api/user/onboarding cuando esté listo
 * Solución temporal: localStorage + Supabase user_preferences sync
 */

const ONBOARDING_KEY = 'pm_onboarding_done';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

const BUYER_OPTIONS = [
  { key: 'equipment', icon: 'Cog', query: 'equipos maquinaria', label: 'Equipos y Maquinaria' },
  { key: 'parts', icon: 'Package', query: 'repuestos componentes', label: 'Repuestos' },
  { key: 'services', icon: 'Wrench', query: 'mantenimiento servicios', label: 'Servicios' },
  { key: 'quotes', icon: 'FileText', query: '', label: 'Solicitar cotización', path: '/comunidad' },
];

const getUserId = () => {
  try {
    const raw = localStorage.getItem('currentUser');
    if (!raw) return null;
    return JSON.parse(raw)?.id ? String(JSON.parse(raw).id) : null;
  } catch { return null; }
};

export const isOnboardingDone = () => {
  try { return localStorage.getItem(ONBOARDING_KEY) === 'true'; } catch { return false; }
};

export const markOnboardingDone = () => {
  try { localStorage.setItem(ONBOARDING_KEY, 'true'); } catch {}
  // Sync to Supabase (fire-and-forget)
  const userId = getUserId();
  if (userId && SUPABASE_URL) {
    fetch(`${SUPABASE_URL}/rest/v1/user_preferences`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
        'Prefer': 'resolution=merge-duplicates,return=minimal',
      },
      body: JSON.stringify({
        user_id: userId,
        preferences: { onboarding_done: true, onboarded_at: Date.now() },
        updated_at: new Date().toISOString(),
      }),
    }).catch(() => {});
  }
};

/**
 * Check Supabase for onboarding status (cross-device).
 * Call on login to restore flag from another device.
 */
export const syncOnboardingFromCloud = async () => {
  if (isOnboardingDone()) return; // Already done locally
  const userId = getUserId();
  if (!userId || !SUPABASE_URL) return;
  try {
    const res = await fetch(
      `${SUPABASE_URL}/rest/v1/user_preferences?user_id=eq.${userId}&select=preferences`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${SUPABASE_ANON_KEY}` } }
    );
    if (!res.ok) return;
    const rows = await res.json();
    if (rows.length > 0 && rows[0].preferences?.onboarding_done) {
      localStorage.setItem(ONBOARDING_KEY, 'true');
    }
  } catch {}
};

const QuickOnboarding = ({ onComplete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0: role, 1: intent (buyer), 2: done

  const handleRole = (role) => {
    // Save role in preferenceEngine state
    try {
      const raw = localStorage.getItem('pm_user_preferences');
      const state = raw ? JSON.parse(raw) : {};
      state.userRole = role;
      state.onboardedAt = Date.now();
      localStorage.setItem('pm_user_preferences', JSON.stringify(state));
    } catch {}

    if (role === 'supplier') {
      markOnboardingDone();
      onComplete?.();
      navigate('/business-registration');
      return;
    }
    setStep(1);
  };

  const handleIntent = (option) => {
    // Track the intent as a search
    if (option.query) {
      preferenceEngine.trackSearch(option.query, 'businesses');
    }
    markOnboardingDone();
    onComplete?.();

    if (option.path) {
      navigate(option.path);
    } else if (option.query) {
      navigate(`/advanced-search-results?tab=businesses&q=${encodeURIComponent(option.query)}`);
    }
  };

  const handleSkip = () => {
    markOnboardingDone();
    onComplete?.();
  };

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden animate-in zoom-in-95 duration-300">

        {step === 0 && (
          <div className="p-6 text-center">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Sparkles" size={24} className="text-primary" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-1">
              {t('onboarding.welcome', '¡Bienvenido a Proveminer!')}
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              {t('onboarding.whatAreYou', '¿Cómo te gustaría usar la plataforma?')}
            </p>

            <div className="space-y-2">
              <button
                onClick={() => handleRole('buyer')}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/20">
                  <Icon name="Search" size={20} className="text-blue-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t('onboarding.buyer', 'Busco proveedores')}</p>
                  <p className="text-xs text-muted-foreground">{t('onboarding.buyerDesc', 'Equipos, repuestos, servicios')}</p>
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground ml-auto" />
              </button>

              <button
                onClick={() => handleRole('supplier')}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:border-green-500/40 hover:bg-green-500/5 transition-all text-left group"
              >
                <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center flex-shrink-0 group-hover:bg-green-500/20">
                  <Icon name="Building2" size={20} className="text-green-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t('onboarding.supplier', 'Soy proveedor')}</p>
                  <p className="text-xs text-muted-foreground">{t('onboarding.supplierDesc', 'Registrar mi empresa')}</p>
                </div>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground ml-auto" />
              </button>
            </div>

            <button onClick={handleSkip} className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors">
              {t('onboarding.skip', 'Solo estoy mirando')}
            </button>
          </div>
        )}

        {step === 1 && (
          <div className="p-6 text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-1">
              {t('onboarding.whatDoYouNeed', '¿Qué necesitas hoy?')}
            </h2>
            <p className="text-xs text-muted-foreground mb-5">
              {t('onboarding.pickOne', 'Elige una opción y te llevamos directo')}
            </p>

            <div className="grid grid-cols-2 gap-2">
              {BUYER_OPTIONS.map(opt => (
                <button
                  key={opt.key}
                  onClick={() => handleIntent(opt)}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all group"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 group-hover:scale-110 transition-all">
                    <Icon name={opt.icon} size={20} className="text-primary" />
                  </div>
                  <span className="text-xs font-medium text-foreground text-center leading-tight">{opt.label}</span>
                </button>
              ))}
            </div>

            <button onClick={handleSkip} className="mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors">
              {t('onboarding.justBrowse', 'Solo quiero explorar')}
            </button>
          </div>
        )}

        {/* Step indicators */}
        <div className="flex justify-center gap-1.5 pb-4">
          <div className={`w-1.5 h-1.5 rounded-full transition-colors ${step === 0 ? 'bg-primary' : 'bg-muted'}`} />
          <div className={`w-1.5 h-1.5 rounded-full transition-colors ${step === 1 ? 'bg-primary' : 'bg-muted'}`} />
        </div>
      </div>
    </div>
  );
};

export default QuickOnboarding;
